import { forwardRef, useCallback } from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  type NavLinkProps,
  Navigate as RouterNavigate,
  useNavigate as useRouterNavigate,
  useLocation as useRouterLocation,
  type LinkProps,
  type NavigateProps,
  type NavigateFunction,
  type To,
} from 'react-router-dom';

import { isBookingIntentSourceKey } from './bookingIntentLineage';
import { hotelPath, useHotelScope } from './scope';

export * from 'react-router-dom';

function scopedTo(clientId: string | undefined, to: To): To {
  return typeof to === 'string'
    ? hotelPath(clientId, to)
    : {
        ...to,
        ...(to.pathname ? { pathname: hotelPath(clientId, to.pathname) } : {}),
      };
}

function scopedState(stateKey: string | undefined, state: unknown) {
  return stateKey && state != null
    ? { __thsHotelStateKey: stateKey, value: state }
    : state;
}

/** Business navigation is always relative to the confirmed client, including anchor hrefs. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const scope = useHotelScope();
  return (
    <RouterLink
      {...props}
      to={scopedTo(scope?.clientId, props.to)}
      state={scopedState(scope?.stateKey, props.state)}
      ref={ref}
    />
  );
});
Link.displayName = 'HotelLink';

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  (props, ref) => {
    const scope = useHotelScope();
    return (
      <RouterNavLink
        {...props}
        to={scopedTo(scope?.clientId, props.to)}
        state={scopedState(scope?.stateKey, props.state)}
        ref={ref}
      />
    );
  },
);
NavLink.displayName = 'HotelNavLink';

export function Navigate(props: NavigateProps) {
  const scope = useHotelScope();
  return (
    <RouterNavigate
      {...props}
      to={scopedTo(scope?.clientId, props.to)}
      state={scopedState(scope?.stateKey, props.state)}
    />
  );
}

export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate();
  const { clientId, stateKey } = useHotelScope() ?? {};
  return useCallback(
    ((to, options) => {
      if (typeof to === 'number') navigate(to);
      else
        navigate(
          scopedTo(clientId, to),
          options?.state == null
            ? options
            : { ...options, state: scopedState(stateKey, options.state) },
        );
    }) as NavigateFunction,
    [navigate, clientId, stateKey],
  );
}

/** Existing hotel layout/filter code sees its path within the selected client. */
export function useLocation() {
  const location = useRouterLocation();
  const { clientId, stateKey } = useHotelScope() ?? {};
  const prefix = clientId ? `/clients/${clientId}` : '';
  const sourceKey =
    typeof location.state?.__thsHotelStateKey === 'string'
      ? location.state.value?.bookingSourceKey
      : undefined;
  // Opaque continuity is re-scoped by actor/company in local storage and by
  // the server. It is not permission to reuse stale offer, guest or draft data.
  const staleSourceState =
    sourceKey === null || isBookingIntentSourceKey(sourceKey)
      ? { bookingSourceKey: sourceKey }
      : null;
  return {
    ...location,
    state: stateKey
      ? location.state?.__thsHotelStateKey === stateKey
        ? location.state.value
        : staleSourceState
      : location.state,
    pathname:
      prefix && location.pathname.startsWith(`${prefix}/`)
        ? location.pathname.slice(prefix.length)
        : location.pathname,
  };
}
